import { RawDraftEntity } from "draft-js";

import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";
import { AtLeastOne } from "src/util/typeHelpers";

export type V2CalleeFieldEntityData = {
  calleeFieldId?: string;
  builtInField?: string | null;
  name?: string;
};

export type InvalidV2CalleeFieldEntityData = {
  isInvalid: true;
  name?: string;
  placeholder?: string;
};

type ValidV2CalleeFieldEntityData = AtLeastOne<V2CalleeFieldEntityData>;

export type V1CalleeFieldEntityData = {
  field?: {
    id: string | null;
    builtInField: string | null;
  };
};

export type CalleeFieldEntity =
  | {
      type: "CALLEE_FIELD";
      mutability: "IMMUTABLE";
      data: V1CalleeFieldEntityData & V2CalleeFieldEntityData;
    }
  | {
      type: "OTHER";
      mutability: string;
      data: unknown;
    };

export const v1CalleeFieldRegex = /{callee_field:[^}]+}/g;

export const convertV1StringToBaseEntity = (
  fieldId: string
): Partial<V2CalleeFieldEntityData> => {
  if (fieldId.startsWith("built_in:")) {
    return {
      calleeFieldId: undefined,
      builtInField: fieldId.substring("built_in:".length) || undefined
    };
  } else {
    return {
      calleeFieldId: fieldId || undefined,
      builtInField: undefined
    };
  }
};

export const convertV1orV2DraftJsEntityToBaseEntity = (
  entity:
    | V1CalleeFieldEntityData
    | { id?: string; builtInField?: string }
    | Partial<V2CalleeFieldEntityData>
    | null
    | undefined
): Partial<V2CalleeFieldEntityData> => {
  if (!entity) {
    throw new Error("Invalid callee field version");
  } else if ("calleeFieldId" in entity) {
    return entity;
  } else if ("field" in entity) {
    return {
      calleeFieldId: entity.field?.id || undefined,
      builtInField: entity.field?.builtInField || undefined
    };
  } else if ("id" in entity) {
    return {
      calleeFieldId: entity.id || undefined,
      builtInField: entity.builtInField || undefined
    };
  }

  throw new Error("Unexpected callee field input received");
};

export const convertAnyCalleeFieldToBaseEntity = (
  field:
    | string // {callee_field:...
    | RawDraftEntity<V1CalleeFieldEntityData | V2CalleeFieldEntityData> // { data: { type: ..., data: { id: ..., builtInField: ... } | { calleeFieldId: ... }, ... }}
    | V1CalleeFieldEntityData // { field: { id: "", builtInField: "" } }
    | V2CalleeFieldEntityData // { calleeFieldId?: "", builtInField?: "" }
    | InvalidV2CalleeFieldEntityData // { isInvalid: true, name?: "", placeholder?: "" }
    | CampaignWithCalleeFields_calleeFields // { __typename: "CampaignCalleeField", id: "", dataSourceFieldName: "", ... }
): V2CalleeFieldEntityData => {
  if (typeof field === "string") {
    return convertV1StringToBaseEntity(field);
  }

  if (
    "field" in field &&
    field.field &&
    ("id" in field.field || "calleeFieldId" in field.field)
  ) {
    return convertV1orV2DraftJsEntityToBaseEntity(field.field);
  }

  if ("data" in field) {
    return convertV1orV2DraftJsEntityToBaseEntity(field.data);
  }

  if ("__typename" in field) {
    return convertDatabaseCalleeFieldToBaseEntity(field);
  }

  if (
    "builtInField" in field ||
    "calleeFieldId" in field ||
    "isInvalid" in field
  ) {
    return field;
  }

  if ("name" in field) {
    return { name: field.name };
  }

  throw new Error(`Invalid callee field version: ${JSON.stringify(field)}`);
};

export const convertDatabaseCalleeFieldToBaseEntity = (
  field: CampaignWithCalleeFields_calleeFields
): Partial<V2CalleeFieldEntityData> => {
  return {
    calleeFieldId: field.id || undefined,
    builtInField: field.builtInField || undefined
  };
};

export const baseCalleeFieldToDraftjsEntity = (
  entity: Partial<V2CalleeFieldEntityData>
): CalleeFieldEntity => {
  return {
    type: "CALLEE_FIELD",
    mutability: "IMMUTABLE",
    data: entity as ValidV2CalleeFieldEntityData
  };
};

export const convertDatabaseFieldToDraftjsEntity = (
  field: CampaignWithCalleeFields_calleeFields
): CalleeFieldEntity => {
  return baseCalleeFieldToDraftjsEntity(
    convertDatabaseCalleeFieldToBaseEntity(field)
  );
};
